import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, ClipboardPaste, FileSpreadsheet, CheckCircle2, XCircle, Radio, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/auth';

const EXPECTED = 'id,name,email,job,location,company,isLinkedInConnection';

function ResultSummary({ res }) {
  if (!res) return null;
  return (
    <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900/70 p-5" data-testid="import-result">
      <div className="flex flex-wrap gap-6">
        <div><p className="text-3xl font-bold text-emerald-400 tabular-nums" data-testid="import-count">{res.imported}</p><p className="text-[11.5px] text-neutral-500">imported → Brevo</p></div>
        <div><p className="text-3xl font-bold text-amber-400 tabular-nums">{res.skipped?.length ?? 0}</p><p className="text-[11.5px] text-neutral-500">saltados</p></div>
        <div><p className="text-3xl font-bold text-red-400 tabular-nums">{res.errors?.length ?? 0}</p><p className="text-[11.5px] text-neutral-500">errores</p></div>
        <div><p className="text-3xl font-bold text-white tabular-nums">{res.total_rows}</p><p className="text-[11.5px] text-neutral-500">rows read</p></div>
      </div>
      {(res.skipped?.length > 0 || res.errors?.length > 0) && (
        <div className="mt-4 max-h-44 overflow-y-auto space-y-1 border-t border-neutral-800 pt-3">
          {(res.skipped || []).map((s, i) => (
            <p key={`s${i}`} className="text-[11.5px] text-amber-400/90">Fila {s.row} · {s.email} — {s.reason}</p>
          ))}
          {(res.errors || []).map((e, i) => (
            <p key={`e${i}`} className="text-[11.5px] text-red-400/90">Fila {e.row} · {e.email} — {e.reason}</p>
          ))}
        </div>
      )}
      {res.imported > 0 && (
        <p className="mt-3 text-[12.5px] text-emerald-400 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> Sincronizados a Brevo con source=stan, agente Stan y etapa NEW_LEAD. Evento LEADS_IMPORTED emitido al bus.
        </p>
      )}
    </div>
  );
}

export default function AdminImportLeadsPage() {
  const [tab, setTab] = useState('file');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const fileRef = useRef(null);

  const handleErr = async (r) => {
    const d = await r.json().catch(() => ({}));
    if (r.status === 401) { window.location.href = '/login?next=%2Fadmin%2Fimport-leads'; return null; }
    throw new Error(d?.detail?.message || d?.detail || `HTTP ${r.status}`);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true); setRes(null); setFileName(file.name);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authFetch('/crm/import/file', { method: 'POST', body: fd });
      if (!r.ok) { await handleErr(r); return; }
      const data = await r.json();
      setRes(data);
      toast.success(`${data.imported} leads imported to Brevo ✅`);
    } catch (e) { toast.error(String(e.message)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const importPaste = async () => {
    if (!text.trim()) { toast.error('Paste the data first (headers on the first line)'); return; }
    setBusy(true); setRes(null);
    try {
      const r = await authFetch('/crm/import/paste', { method: 'POST', body: JSON.stringify({ text }) });
      if (!r.ok) { await handleErr(r); return; }
      const data = await r.json();
      setRes(data);
      toast.success(`${data.imported} leads imported to Brevo ✅`);
    } catch (e) { toast.error(String(e.message)); }
    finally { setBusy(false); }
  };

  const tabBtn = (id, icon, label) => (
    <button onClick={() => { setTab(id); setRes(null); }}
      className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-[13px] font-semibold border transition-colors ${tab === id ? 'bg-[#F77F11] border-[#F77F11] text-white' : 'border-neutral-700 text-neutral-300 hover:border-neutral-500'}`}
      data-testid={`import-tab-${id}`}>
      {icon} {label}
    </button>
  );

  return (
    <div className="pt-32 pb-24 min-h-screen bg-neutral-950 text-white" data-testid="admin-import-leads-page">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="kx-eyebrow mb-2">STAN PIPELINE</p>
            <h1 className="text-3xl font-bold">Import Leads</h1>
            <p className="mt-2 text-sm text-neutral-400 max-w-xl">
              Upload Stan's report (CSV or Excel) or paste the data. Every lead is validated (invalid/disposable emails out),
              saved to the local CRM and synced to <span className="text-white font-semibold">Brevo</span> tagged: source <code className="text-[#ffb374]">stan</code>, agent Stan, stage NEW_LEAD.
            </p>
          </div>
          <Button asChild variant="outline" className="border-neutral-700 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-full h-10">
            <Link to="/admin/mission-control" data-testid="import-link-mc"><Radio className="w-4 h-4 mr-1.5" /> Mission Control</Link>
          </Button>
        </div>

        <div className="mt-6 rounded-xl border border-sky-500/20 bg-sky-500/5 px-4 py-3">
          <p className="text-[12px] text-sky-300">Expected Stan format (headers are auto-detected, in any order):</p>
          <code className="text-[12px] text-white font-mono">{EXPECTED}</code>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {tabBtn('file', <FileSpreadsheet className="w-4 h-4" />, 'CSV / Excel file')}
          {tabBtn('paste', <ClipboardPaste className="w-4 h-4" />, 'Copy & paste')}
        </div>

        {tab === 'file' ? (
          <div
            className="mt-5 rounded-2xl border-2 border-dashed border-neutral-700 hover:border-[#F77F11]/60 bg-neutral-900/50 p-10 text-center transition-colors cursor-pointer"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); uploadFile(e.dataTransfer.files?.[0]); }}
            data-testid="import-dropzone"
          >
            <Upload className="w-10 h-10 text-[#F77F11] mx-auto" />
            <p className="mt-3 font-semibold">{busy ? `Importing ${fileName}…` : 'Drag the file here or click to choose it'}</p>
            <p className="mt-1 text-[12px] text-neutral-500">.csv · .xlsx · max 5MB · up to 1,000 leads per import</p>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="import-file-input" />
          </div>
        ) : (
          <div className="mt-5">
            <textarea value={text} onChange={(e) => setText(e.target.value)}
              placeholder={`${EXPECTED}\n1,Maria Lopez,maria@lataqueria.com,Owner,San Francisco,La Taquería,TRUE`}
              className="w-full h-56 rounded-2xl bg-neutral-900 border border-neutral-700 focus:border-[#F77F11] outline-none p-4 font-mono text-[12.5px] text-neutral-200"
              data-testid="import-paste-textarea" />
            <Button onClick={importPaste} disabled={busy}
              className="mt-3 w-full bg-[#F77F11] hover:bg-[#d8651a] rounded-full h-12 font-bold" data-testid="import-paste-btn">
              <ArrowRight className="w-4 h-4 mr-1.5" /> {busy ? 'Importing…' : 'Import pasted → Brevo'}
            </Button>
            <p className="mt-1.5 text-[11.5px] text-neutral-500">Accepts commas or tabs (pasting straight from Excel/Sheets works). First line = headers.</p>
          </div>
        )}

        <ResultSummary res={res} />
      </div>
    </div>
  );
}
